/* PackFlow header extras
 * - Adds a "My Account" entry to the hamburger drawer (and a header pill)
 *   that points at /pages/my-account, where the real PackFlow dashboard lives.
 *   Shopify's avatar in the top-right only opens Shopify's hosted account
 *   sheet (Orders / Profile), which has no concept of PackFlow tiers.
 * - Hides the auto-generated /collections/all "Catalog" item from the menu
 *   and redirects the URL if anyone navigates there directly.
 *
 * Loaded from layout/theme.liquid so it runs on every storefront page.
 */
(function () {
  'use strict';
  if (window._pfHeaderExtrasLoaded) return;
  window._pfHeaderExtrasLoaded = true;

  // ---------------------------------------------------------------------------
  // 1. /collections/all + /search hard redirect — runs as early as possible.
  //    Both pages leak the product grid (4 print SKUs + 3 plans) which we
  //    don't want public. Server-side redirects on /collections/* are also
  //    set up via Shopify Admin URL Redirects (Jacques 2026-05-09).
  // ---------------------------------------------------------------------------
  if (location.pathname === '/collections/all'
   || location.pathname === '/collections'
   || location.pathname.startsWith('/collections/')
   || location.pathname === '/search') {
    location.replace('/');
    return;
  }

  // ---------------------------------------------------------------------------
  // 1.5  Cart-quantity input fix — always runs so 3-digit quantities like
  //      224 (rush per-sticker) don't get visually truncated to "22" by the
  //      theme's fixed-width quantity input.
  // ---------------------------------------------------------------------------
  (function injectCartQtyFix() {
    if (document.getElementById('pf-cart-qty-fix')) return;
    var s = document.createElement('style');
    s.id = 'pf-cart-qty-fix';
    s.textContent = [
      '.cart__row .quantity input[type="number"],',
      '.cart-item .quantity input[type="number"],',
      'cart-items quantity-input input[type="number"],',
      'quantity-input input[type="number"],',
      'input.quantity__input,',
      'input[name="updates[]"]{',
      '  min-width:54px!important;width:auto!important;',
      '  padding-left:6px!important;padding-right:6px!important;',
      '  text-align:center;box-sizing:content-box;',
      '}'
    ].join('');
    (document.head || document.documentElement).appendChild(s);
  })();

  // ---------------------------------------------------------------------------
  // 1.6  Hide the search icon entirely. The theme's predictive-search shows
  //      a default product grid as soon as the search drawer opens — even
  //      with an empty query. That grid was leaking the 4 print SKUs + 3
  //      subscription products to anyone tapping the magnifier icon. We have
  //      a tiny catalog so search is unnecessary anyway. Jacques 2026-05-09.
  // ---------------------------------------------------------------------------
  (function hideSearch() {
    if (document.getElementById('pf-hide-search')) return;
    var s = document.createElement('style');
    s.id = 'pf-hide-search';
    s.textContent = [
      '.search-action,',
      '.search-modal,',
      'a[href="/search"],',
      'a[href^="/search?"],',
      '.menu-drawer__menu a[href*="/search"]{',
      '  display:none!important;visibility:hidden!important;',
      '}'
    ].join('');
    (document.head || document.documentElement).appendChild(s);
  })();

  // Detect login state. Prefer window.PF_CUSTOMER (set by theme.liquid from
  // the {% if customer %} Liquid object) because Shopify's own JSON's
  // shape can drift. Fall back to the Shopify customer-account JSON.
  function readCustomer() {
    if (window.PF_CUSTOMER) return window.PF_CUSTOMER;
    try {
      var el = document.getElementById('shopify-customer-account-data');
      if (!el) return null;
      var data = JSON.parse(el.textContent || el.innerText || '{}');
      return data.customer || null;
    } catch (_) { return null; }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ---------------------------------------------------------------------------
  // 2. Drawer surgery
  // ---------------------------------------------------------------------------
  function patchDrawer() {
    // Hide the "Catalog" entry that points at the auto-generated all-products page.
    document.querySelectorAll(
      'a[href="/collections/all"], a[href$="/collections/all"], #HeaderDrawer-catalog'
    ).forEach(function (a) {
      var li = a.closest('li');
      if (li) li.style.display = 'none';
      else a.style.display = 'none';
    });

    // Add "My Account" / "Sign in" to the drawer if it isn't already there.
    var drawerList = document.querySelector('header-drawer .menu-drawer__menu, header-drawer ul.menu-drawer__menu, .menu-drawer__menu');
    if (!drawerList) return;
    if (drawerList.querySelector('[data-pf-account-link]')) return; // already inserted

    var customer = readCustomer();
    var isLoggedIn = !!customer;

    var li = document.createElement('li');
    li.className = 'menu-drawer__list-item--flat';
    li.setAttribute('data-pf-account-link', '1');
    li.style.setProperty('--menu-drawer-animation-index', '20');

    var a = document.createElement('a');
    a.href = isLoggedIn
      ? '/pages/my-account'
      : 'https://account.gopackflow.com/?return_to=' + encodeURIComponent('/pages/my-account');
    a.className = 'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element focus-inset';

    var span = document.createElement('span');
    span.className = 'menu-drawer__menu-item-text wrap-text';
    span.textContent = isLoggedIn ? 'My Account' : 'Sign In';

    a.appendChild(span);
    li.appendChild(a);
    drawerList.appendChild(li);
  }

  // ---------------------------------------------------------------------------
  // 3. Header pill ("Dashboard" / "Sign In") on the LEFT side of the header,
  //    next to the hamburger / drawer trigger. Jacques 2026-05-09 wanted
  //    this on the left, between the menu and the logo, so it's the first
  //    thing customers see (especially logged-out visitors who need to
  //    reach the dashboard / login flow).
  // ---------------------------------------------------------------------------
  function patchHeader() {
    if (document.querySelector('[data-pf-header-dashboard]')) return;
    if (location.pathname === '/pages/my-account') return; // already there

    var customer = readCustomer();
    var isLoggedIn = !!customer;

    // LEFT-side mount: prefer the left header column (Horizon theme) so the
    // pill sits next to the hamburger / drawer button and the logo.
    var leftCol = document.querySelector('.header__column--left, .header-actions__column--left');
    var rightCol = document.querySelector('header-actions, .header__column--right, .header-actions, .header__actions');
    var mount = leftCol || rightCol;
    if (!mount) return;

    var a = document.createElement('a');
    a.href = isLoggedIn
      ? '/pages/my-account'
      : 'https://account.gopackflow.com/?return_to=' + encodeURIComponent('/pages/my-account');
    a.setAttribute('data-pf-header-dashboard', '1');
    a.className = 'pf-dashboard-pill';
    a.setAttribute('aria-label', isLoggedIn ? 'PackFlow account dashboard' : 'Sign in to PackFlow');
    var label = isLoggedIn ? 'Dashboard' : 'Sign In';
    a.innerHTML = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px;vertical-align:-2px;"><path d="M3 12 12 3l9 9"/><path d="M5 10v10h14V10"/></svg><span class="pf-dashboard-pill-label">' + label + '</span>';
    // Append to the left column (sits AFTER the hamburger trigger by default).
    if (leftCol) {
      leftCol.appendChild(a);
    } else {
      mount.insertBefore(a, mount.firstChild);
    }

    if (!document.getElementById('pf-dashboard-pill-style')) {
      var style = document.createElement('style');
      style.id = 'pf-dashboard-pill-style';
      style.textContent = [
        '.pf-dashboard-pill{',
        '  display:inline-flex;align-items:center;justify-content:center;',
        '  gap:0;padding:6px 12px;margin-left:10px;',
        '  font-family:"Barlow Condensed",Arial,sans-serif;font-weight:800;font-size:13px;',
        '  letter-spacing:1px;text-transform:uppercase;text-decoration:none;color:#fff;',
        '  background:#FF5500;border-radius:999px;line-height:1;white-space:nowrap;',
        '  box-sizing:border-box;flex:0 0 auto;',
        '  transition:transform .15s ease,filter .15s ease;',
        '}',
        '.pf-dashboard-pill:hover{transform:translateY(-1px);filter:brightness(1.08);color:#fff;}',
        '.pf-dashboard-pill .pf-dashboard-pill-label{display:inline-block;color:inherit;line-height:1;}',
        // Narrow phones — icon-only, no text. Mobile real estate is tight
        // and the house icon already communicates the action. (Jacques 2026-05-09:
        // tried the full word, it didn\'t fit cleanly, going back to icon-only
        // on mobile like the original implementation.)
        '@media (max-width: 749px){',
        '  .pf-dashboard-pill .pf-dashboard-pill-label{display:none!important;}',
        '  .pf-dashboard-pill{padding:8px;}',
        '  .pf-dashboard-pill svg{width:16px;height:16px;margin-right:0!important;}',
        '}'
      ].join('');
      document.head.appendChild(style);
    }
  }

  // ---------------------------------------------------------------------------
  // 4. Run + observe (Shopify's drawer can re-render after sticky-scroll, etc.)
  // ---------------------------------------------------------------------------
  ready(function () {
    patchDrawer();
    patchHeader();

    // Re-apply if the header re-renders for any reason
    var mo = new MutationObserver(function () {
      patchDrawer();
      patchHeader();
    });
    var hdr = document.querySelector('header') || document.body;
    mo.observe(hdr, { childList: true, subtree: true });
  });
})();

